import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { MarcoPlantilla } from './MarcoPlantilla';
import { plantillaPorId, TusRedesEditor, type ImagenEstudio, type ProductoParaRedes } from './TusRedesEditor';
import { PLANTILLAS } from '../../../diseno/plantillas/catalog';
import { resolverMarca, slotsIniciales } from '../../../utils/diseno/marca';
import { grupoOcasion, ocasionesOrdenadas } from '../../../utils/diseno/ocasiones';
import type { EmpresaMarcaInput } from '../../../utils/diseno/types';
import { cargarTusRedes, estadoTusRedes, suscribirTusRedes, type DisenoRedes } from '../../../stores/tusRedesStore';

const ORDEN = [
  'promocion',
  'nuevo-producto',
  'destacado',
  'catalogo',
  'combo',
  'festivo',
  'medios-de-pago',
  'envios',
] as const;

const NOMBRES: Record<string, string> = {
  promocion: 'Promoción',
  'nuevo-producto': 'Nuevo producto',
  destacado: 'Producto destacado',
  catalogo: 'Catálogo',
  combo: 'Combo',
  festivo: 'Festivo',
  'medios-de-pago': 'Medios de pago',
  envios: 'Domicilios',
};

interface Props {
  empresa: EmpresaMarcaInput & { id: string };
  productos: ProductoParaRedes[];
  imagenesEstudio: ImagenEstudio[];
  /** Foto recién generada. Se coloca en la imagen del producto al abrir una plantilla. */
  fotoLista?: string | null;
}

export default function TusRedesPanel({ empresa, productos, imagenesEstudio, fotoLista }: Props) {
  const snap = useSyncExternalStore(suscribirTusRedes, estadoTusRedes, estadoTusRedes);
  const [categoria, setCategoria] = useState<string>('todas');
  const [ocasion, setOcasion] = useState<string>('todas');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [diseno, setDiseno] = useState<DisenoRedes | null>(null);

  useEffect(() => {
    void cargarTusRedes(empresa.id);
  }, [empresa.id]);

  const marca = useMemo(() => resolverMarca(empresa), [empresa]);
  const categorias = ORDEN.filter((id) => PLANTILLAS.some((p) => p.manifest.categoria === id));
  const ocasiones = useMemo(() => ocasionesOrdenadas(PLANTILLAS), []);
  const visibles = PLANTILLAS.filter(
    (p) =>
      (categoria === 'todas' || p.manifest.categoria === categoria) &&
      (categoria !== 'festivo' || ocasion === 'todas' || grupoOcasion(p) === ocasion)
  );
  const plantilla = abierta ? PLANTILLAS.find((p) => p.id === abierta) : undefined;
  const guardados = snap.empresaId === empresa.id ? snap.disenos : [];
  const imagenes = snap.empresaId === empresa.id ? snap.imagenes : [];

  if (plantilla) {
    return (
      <TusRedesEditor
        plantilla={plantilla}
        empresa={empresa}
        productos={productos}
        imagenesEstudio={imagenesEstudio}
        imagenesGuardadas={imagenes}
        fotoInicial={diseno ? null : fotoLista}
        inicial={diseno && diseno.plantillaId === plantilla.id ? diseno : null}
        onClose={() => {
          setAbierta(null);
          setDiseno(null);
        }}
        onGuardado={() => {
          void cargarTusRedes(empresa.id);
        }}
      />
    );
  }

  return (
    <div data-testid="tus-redes-panel" className="space-y-4">
      {fotoLista && (
        <p className="text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          Elige una plantilla. La foto generada se pone en la imagen del producto.
        </p>
      )}
      <p className="text-sm text-gray-600 max-w-2xl">
        Elige una plantilla, pon tus fotos y tu texto, y toca Usar mi marca para el logo y los colores de Configuración.
        Descarga la imagen y publícala en tus redes.
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        <Chip activo={categoria === 'todas'} onClick={() => setCategoria('todas')}>
          Todas
        </Chip>
        {categorias.map((id) => (
          <Chip
            key={id}
            activo={categoria === id}
            onClick={() => {
              setCategoria(id);
              setOcasion('todas');
            }}
          >
            {NOMBRES[id] || id}
          </Chip>
        ))}
      </div>
      {categoria === 'festivo' && ocasiones.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="tus-redes-ocasiones">
          <Chip activo={ocasion === 'todas'} onClick={() => setOcasion('todas')} pequeno>
            Todas las fechas
          </Chip>
          {ocasiones.map((o) => (
            <Chip key={o.id} activo={ocasion === o.id} onClick={() => setOcasion(o.id)} pequeno>
              {o.nombre}
            </Chip>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {visibles.map((p) => (
          <Tarjeta
            key={`${p.id}-${p.formato}`}
            nombre={p.manifest.nombre}
            categoria={p.manifest.ocasion_nombre || NOMBRES[p.manifest.categoria] || p.manifest.categoria}
            onAbrir={() => {
              setDiseno(null);
              setAbierta(p.id);
            }}
            html={p.html}
            formato={p.formato}
            slots={slotsIniciales(p.manifest, marca)}
            palette={p.manifest.paleta_temporada || marca.palette}
          />
        ))}
      </div>

      {guardados.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#0B1F4A] mb-2">Mis publicaciones</h3>
          <ul className="divide-y border border-gray-200 rounded-lg bg-white">
            {guardados.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{d.nombre}</p>
                  <p className="text-xs text-gray-500">{d.estado === 'descargada' ? 'Descargada' : 'Borrador'}</p>
                </div>
                <button
                  type="button"
                  className="min-h-[44px] px-3 text-sm text-[#1A56DB]"
                  onClick={() => {
                    if (!plantillaPorId(d.plantillaId, d.formato)) return;
                    setDiseno(d);
                    setAbierta(d.plantillaId);
                  }}
                >
                  Usar de nuevo
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Chip({
  activo,
  onClick,
  children,
  pequeno,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
  pequeno?: boolean;
}) {
  const colores = pequeno
    ? activo
      ? 'bg-[#1A56DB] text-white'
      : 'bg-white border border-gray-200 text-gray-600'
    : activo
      ? 'bg-[#0B1F4A] text-white'
      : 'bg-white border border-gray-300 text-gray-700';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 min-h-[44px] px-3 rounded-full text-sm font-medium ${colores}`}
    >
      {children}
    </button>
  );
}

function Tarjeta({
  nombre,
  categoria,
  onAbrir,
  html,
  formato,
  slots,
  palette,
}: {
  nombre: string;
  categoria: string;
  onAbrir: () => void;
  html: string;
  formato: string;
  slots: Record<string, string>;
  palette: ReturnType<typeof resolverMarca>['palette'];
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: '240px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onAbrir}
      className="text-left bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
    >
      <div className="bg-gray-100 flex justify-center py-2 min-h-[120px]">
        {visible ? (
          <MarcoPlantilla html={html} formato={formato} slots={slots} palette={palette} ajustes={{}} ancho={168} titulo={nombre} />
        ) : (
          <span className="text-xs text-gray-400 self-center">{categoria}</span>
        )}
      </div>
      <div className="px-2 py-2">
        <p className="text-[11px] uppercase tracking-wide text-gray-400">{categoria}</p>
        <p className="text-sm font-medium text-[#0B1F4A] leading-snug">{nombre}</p>
      </div>
    </button>
  );
}
